<template>
<div class="users">
    <div class="page__header">
        <div class="page__title">{{ $t(`pageTitles.${"users"}`) }}</div>
        <div class="page__right">
            <div class="selected-filters" v-if="selectedTeam && selectedTeam.length > 0">
                <div  
                    class="selected-filters__filter" 
                    v-for="teamId in selectedTeam" 
                    :key="`team-${teamId}`"
                >
                    <span>Team: {{ getTeamName(teamId) }}</span>
                    <div class="selected-filters__remove" @click="removeTeamTag(teamId)"></div>
                </div>

                <div class="selected-filters__filter" v-if="selectedStatus !== null">
                    <span>Status: {{ selectedStatus == 1 ? 'Active' : 'Inactive' }}</span>
                    <div class="selected-filters__remove" @click="removeStatusTag()"></div>
                </div>
            </div> 
            <form class="search search--filter" @submit.prevent="applyFilters()">
                <div class="search__wrapper">
                    <input class="search__search" :class="{ 'search--active': searchKeywords.length }" type="text" v-model="searchKeywords" />
                    <div class="search__icon"></div>
                    <div class="search__splitter"></div>
                </div>
                <div class="search__filter" ref="filterToggler" @click="showSearchFilter = !showSearchFilter"></div>
                <SearchFilter v-if="showSearchFilter" :filterToggler="filterToggler" :searchType="'user'" :toggleSearchFilter="
                (state) => {
                  showSearchFilter = state;
                }
              " />
            </form>

            <button v-if="instance.proxy.$checkRolePermission('user_create')" class="users__button button button--default" @click="store.commit('setShowAddUserModal', true)">
                {{ $t(`settings.user.${"addUserBtn"}`) }}
            </button>
        </div>
    </div>

    <div class="table">
        <Loading v-if="usersLoading" />
        <template v-else>
            <NoData v-if="!users.length" :type="'users'" />
            <template v-else>
                <div class="table__header box box--default">
                    <div class="table__label table__label--small"></div>
                    <div class="table__label table__label--large">
                        {{ $t(`users.${"tableHeader"}.${"name"}`) }}
                    </div>
                    <div class="table__label table__label--medium">
                        {{ $t(`users.${"tableHeader"}.${"contact"}`) }}
                    </div>
                    <div class="table__label table__label--large">
                        {{ $t(`users.${"tableHeader"}.${"team"}`) }}
                    </div>
                    <div class="table__label table__label--large">
                        {{ $t(`users.${"tableHeader"}.${"companyName"}`) }}
                    </div>
                    <div class="table__label table__label--large">
                        {{ $t(`users.${"tableHeader"}.${"address"}`) }}
                    </div>
                    <div class="table__label table__label--small">
                        {{ $t(`users.${"tableHeader"}.${"zipCode"}`) }}
                    </div>
                    <div class="table__label table__label--medium">
                        {{ $t(`users.${"tableHeader"}.${"city"}`) }}
                    </div>
                    <div class="table__label table__label--medium">
                        {{ $t(`users.${"tableHeader"}.${"supervisor"}`) }}
                    </div>
                    <div class="table__label table__label--medium">
                        {{ $t(`users.${"tableHeader"}.${"salesGroup"}`) }}
                    </div>
                    <div class="table__label table__label--medium">
                        {{ $t(`users.${"tableHeader"}.${"account"}`) }}
                    </div>
                    <div class="table__label table__label--small" v-if="instance.proxy.$checkRolePermission('user_delete')"></div>
                </div>

                <div class="table__body">
                    <User v-for="user in users" :key="user.id" :user="user" :deleteUser="deleteUser" />
                </div>
            </template>
        </template>
    </div>
    <Pagination :limit="3" :data="usersPerPage" @pagination-change-page="getResults"></Pagination>
</div>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import User from "@/components/Users/User";
import Loading from "@/partials/Loading";
import NoData from "@/partials/NoData";
import SearchFilter from "@/partials/SearchFilter";

const store = useStore();
const route = useRoute();
const router = useRouter();
const instance = getCurrentInstance();

// Template refs
const filterToggler = ref(null);

// Reactive data
const searchKeywords = ref("");
const showSearchFilter = ref(false);
const selectedTeam = ref(null);
const selectedStatus = ref(null);

// Computed properties
const usersLoading = computed(() => store.getters.usersLoading);
const usersPerPage = computed(() => store.getters.usersPerPage);
const users = computed(() => Array.isArray(usersPerPage.value?.data) ? usersPerPage.value.data : []);
const loggedUser = computed(() => store.getters.loggedUser);

// Methods
const syncFiltersFromRoute = (query) => {
  searchKeywords.value = query.search || "";
  
  if (query.teams) {
    const teams = Array.isArray(query.teams) ? query.teams : String(query.teams).split(",");
    selectedTeam.value = teams.map(id => Number(id));
  } else {
    selectedTeam.value = [];
  }

  selectedStatus.value = (query.active_users !== undefined && query.active_users !== "") ? query.active_users : null;
};

const getTeamName = (id) => {
  if (loggedUser.value && loggedUser.value.teams) {
    const team = loggedUser.value.teams.find(t => t.id === id);
    return team ? team.name : `Team ${id}`;
  } else {
    return;
  }
};

const removeTeamTag = (id) => {
  selectedTeam.value = selectedTeam.value.filter(teamId => teamId !== id);
  applyFilters();
};

const removeStatusTag = () => {
  selectedStatus.value = null;
  applyFilters();
};

const applyFilters = () => {
  router.replace({
    query: {
      ...route.query,
      page: 1,
      search: searchKeywords.value || undefined,
      teams: selectedTeam.value.length ? selectedTeam.value.join(",") : undefined,
      active_users: selectedStatus.value !== null ? selectedStatus.value : undefined
    }
  });
};

const deleteUser = ({ display, message, action, id }) => {
  store.commit("setDeleteModal", {
    display,
    message,
    action,
    id,
  });
};

const searchUsers = async () => {
  store.commit("setUsersLoading", true);
  try {
    const q = route.query;
    const page = Number(q.page) || 1;

    const hasFilters = q.search || q.teams || (q.active_users !== undefined && q.active_users !== null);

    if (hasFilters) {
      await store.dispatch("searchUsers", {
        searchKeywords: q.search || "",
        selectedTeam: q.teams ? q.teams.split(",").map(Number) : [],
        active_users: q.active_users,
        page,
      });
    } else {
      await store.dispatch("getUsersPage", { page });
    }
  } finally {
    store.commit("setUsersLoading", false);
  }
};

const getResults = async (paginationPage) => {
  const currentPage = Number(route.query.page) || 1;
  if (Number(paginationPage) === currentPage) return;

  router.replace({
    query: { ...route.query, page: paginationPage },
  });
};

// Watchers
watch(
  () => route.query,
  async (query, _oldQuery) => {
    if (!query.page) {
      router.replace({
        query: { ...query, page: 1 },
      });
      return;
    }

    if (_oldQuery && JSON.stringify(query) === JSON.stringify(_oldQuery)) {
      return;
    }

    syncFiltersFromRoute(query);

    await searchUsers();
  },
  { immediate: true, deep: true }
);

// Lifecycle
onMounted(() => {
  if (!instance.proxy.$checkRolePermission('user_access')) {
    router.push('/dashboard');
  }
});
</script>

<style lang="scss" scoped>
.users {
    height: 100%;

    &__button {
        width: 190px;
    }

    .table {
        margin-bottom: 20px;
        height: calc(100% - 40px - 20px - 20px - 30px);
    }
}

.search-filter {
    max-width: 600px !important;
}
</style>
